import React, { useEffect, useMemo, useState } from 'react';
import { RiskCard, RiskCardHeader, RiskCardBody, RiskButton, RiskBadge, RiskChip } from '../../components/risk/UiKit';

export default function Workflows() {
  const API_BASE = process.env.REACT_APP_API_URL + '/api';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workflows, setWorkflows] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [activeCadence, setActiveCadence] = useState('all');
  const [activeYear, setActiveYear] = useState('all');

  const [form, setForm] = useState({
    name: '',
    cadence: 'monthly',
    start_on: '',
    assessment_id: '',
    targets: [] // { department_id?, domain_id? }
  });

  const canCreate = true; // gated by nav; backend re-validates

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [wfRes, asmtRes, deptRes] = await Promise.all([
          fetch(`${API_BASE}/risk-assessment/workflows`, { credentials: 'include' }),
          fetch(`${API_BASE}/risk-assessment/assessments`, { credentials: 'include' }),
          fetch(`${API_BASE}/departments/with-aliases`, { credentials: 'include' })
        ]);
        const wf = await wfRes.json();
        const asmt = await asmtRes.json();
        const depts = await deptRes.json();
        if (wf.success) setWorkflows(wf.workflows || []);
        if (asmt.success) setAssessments(asmt.assessments || []);
        setDepartments(Array.isArray(depts) ? depts : []);
      } catch (e) {
        setError('Failed to load workflows');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCreate = async () => {
    if (!form.name || !form.start_on) return;
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/workflows`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const json = await res.json();
      if (json.success) {
        setWorkflows([json.workflow, ...workflows]);
        setForm({ name: '', cadence: 'monthly', start_on: '', assessment_id: '', targets: [] });
      }
    } catch (_) {}
  };

  const generateRun = async (workflowId) => {
    // Simple: create one run for the current month
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0,10);
    const end = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().slice(0,10);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/workflows/${workflowId}/generate-runs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period_start: start, period_end: end })
      });
      const json = await res.json();
      if (json.success) {
        // Optionally refresh or show a toast; keeping minimal
      }
    } catch (_) {}
  };

  // Categories and filtering
  const cadenceOptions = [
    { key: 'all', label: 'All' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'quarterly', label: 'Quarterly' },
    { key: 'custom', label: 'Custom' }
  ];
  const yearOptions = useMemo(() => {
    const years = workflows
      .map(w => (w.start_on ? new Date(w.start_on).getFullYear() : null))
      .filter(y => y);
    return Array.from(new Set(years)).sort((a, b) => b - a);
  }, [workflows]);
  const filteredWorkflows = useMemo(() => {
    return workflows.filter(w => {
      const year = w.start_on ? new Date(w.start_on).getFullYear() : null;
      const cadenceOk = activeCadence === 'all' || w.cadence === activeCadence;
      const yearOk = activeYear === 'all' || year === activeYear;
      return cadenceOk && yearOk;
    });
  }, [workflows, activeCadence, activeYear]);

  const statDefs = [
    { label: 'Total', value: workflows.length },
    { label: 'Monthly', value: workflows.filter(w => w.cadence === 'monthly').length },
    { label: 'Quarterly', value: workflows.filter(w => w.cadence === 'quarterly').length },
    { label: 'Custom', value: workflows.filter(w => w.cadence === 'custom').length }
  ];

  return (
    <div className="h-full bg-gray-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* Modern Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Workflows</h1>
              <p className="text-gray-600">Schedule periodic monitoring runs and send in-app reminders</p>
            </div>
            {canCreate && (
              <RiskButton className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700" onClick={() => {
                const el = document.getElementById('create-workflow');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}>New Workflow</RiskButton>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl text-sm font-medium bg-red-50 text-red-800 border border-red-200">{error}</div>
        )}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Categories + Stats */}
          <div className="lg:col-span-3 space-y-6">
            <RiskCard>
              <RiskCardHeader title="Categories" subtitle="Filter workflows" />
              <RiskCardBody className="space-y-6">
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Cadence</div>
                  <div className="flex flex-wrap gap-2">
                    {cadenceOptions.map(opt => (
                      <RiskChip key={opt.key} active={activeCadence === opt.key} onClick={() => setActiveCadence(opt.key)}>
                        {opt.label}
                      </RiskChip>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Start Year</div>
                  <div className="flex flex-wrap gap-2">
                    <RiskChip active={activeYear === 'all'} onClick={() => setActiveYear('all')}>All</RiskChip>
                    {yearOptions.map(y => (
                      <RiskChip key={y} active={activeYear === y} onClick={() => setActiveYear(y)}>{y}</RiskChip>
                    ))}
                  </div>
                </div>
              </RiskCardBody>
            </RiskCard>

            {workflows.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {statDefs.map(s => (
                  <RiskCard key={s.label}>
                    <RiskCardBody>
                      <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                      <div className="text-sm text-slate-600">{s.label}</div>
                    </RiskCardBody>
                  </RiskCard>
                ))}
              </div>
            )}
          </div>

          {/* Right: Main */}
          <div className="lg:col-span-9 space-y-6">
            {canCreate && (
              <RiskCard id="create-workflow">
                <RiskCardHeader title="Create Workflow" subtitle="Monthly/quarterly monitoring runs" />
                <RiskCardBody>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Name</label>
                      <input className="w-full border border-slate-300 rounded-lg px-3 py-2" value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Cadence</label>
                      <select className="w-full border border-slate-300 rounded-lg px-3 py-2" value={form.cadence} onChange={(e)=>setForm(f=>({...f,cadence:e.target.value}))}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Start On</label>
                      <input type="date" className="w-full border border-slate-300 rounded-lg px-3 py-2" value={form.start_on} onChange={(e)=>setForm(f=>({...f,start_on:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Assessment (optional)</label>
                      <select className="w-full border border-slate-300 rounded-lg px-3 py-2" value={form.assessment_id} onChange={(e)=>setForm(f=>({...f,assessment_id:e.target.value}))}>
                        <option value="">None</option>
                        {assessments.map(a=> (
                          <option key={a.id} value={a.id}>{a.title} ({a.assessment_year})</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Targets (departments only for now) */}
                  <div className="mt-4">
                    <label className="block text-sm text-slate-600 mb-2">Departments to monitor</label>
                    <div className="flex flex-wrap gap-2">
                      {departments.map(d => {
                        const selected = form.targets.some(t => t.department_id === d.id);
                        return (
                          <RiskChip
                            key={d.id}
                            active={selected}
                            onClick={() => setForm(f=>{
                              const exists = f.targets.some(t=>t.department_id===d.id);
                              return exists
                                ? { ...f, targets: f.targets.filter(t=>t.department_id!==d.id) }
                                : { ...f, targets: [...f.targets, { department_id: d.id }] };
                            })}
                          >
                            {d.name}
                          </RiskChip>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-4">
                    <RiskButton onClick={handleCreate}>Create Workflow</RiskButton>
                  </div>
                </RiskCardBody>
              </RiskCard>
            )}

            <RiskCard>
              <RiskCardHeader
                title="Existing Workflows"
                subtitle={filteredWorkflows.length !== workflows.length ? `${filteredWorkflows.length} of ${workflows.length} shown` : `${workflows.length} total`}
              />
              {loading ? (
                <RiskCardBody>
                  <div className="text-slate-500">Loading...</div>
                </RiskCardBody>
              ) : workflows.length === 0 ? (
                <RiskCardBody>
                  <div className="p-8 text-center">
                    <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v8m-4-4h8M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
                      </svg>
                    </div>
                    <div className="text-slate-900 font-medium mb-1">No workflows yet</div>
                    <div className="text-slate-600 text-sm mb-3">Create your first workflow to start periodic monitoring</div>
                    {canCreate && (
                      <RiskButton onClick={() => {
                        const el = document.getElementById('create-workflow');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}>Create Workflow</RiskButton>
                    )}
                  </div>
                </RiskCardBody>
              ) : (
                <div className="p-6">
                  {filteredWorkflows.length === 0 ? (
                    <div className="text-center text-slate-600">No workflows match the selected category.</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {filteredWorkflows.map(wf => (
                        <div key={wf.id} className="group bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-all">
                          <div className="flex items-start justify-between mb-2">
                            <div className="font-semibold text-slate-900 truncate mr-2">{wf.name}</div>
                            <RiskBadge color={wf.cadence === 'monthly' ? 'blue' : wf.cadence === 'quarterly' ? 'amber' : 'slate'}>{wf.cadence}</RiskBadge>
                          </div>
                          <div className="text-sm text-slate-600">Start: {wf.start_on ? new Date(wf.start_on).toLocaleDateString() : '—'}</div>
                          <div className="mt-4 flex items-center justify-end">
                            <RiskButton variant="outline" onClick={() => generateRun(wf.id)}>Generate Run</RiskButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </RiskCard>
          </div>
        </div>
      </div>
    </div>
  );
}


